import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Heart, Star, RotateCcw, Zap, SlidersHorizontal, Sparkles } from 'lucide-react';
import { ProfileCard } from './ProfileCard';
import { ProfileDetailModal } from './ProfileDetailModal';
import { FilterModal } from './FilterModal';
import { BoostModal } from './BoostModal';
import { User } from '../types';
interface DiscoverViewProps {
  profiles: User[];
  onMatch: (user: User) => void;
}
export function DiscoverView({
  profiles,
  onMatch
}: DiscoverViewProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showBoost, setShowBoost] = useState(false);
  const currentProfile = profiles[currentIndex];
  const nextProfile = profiles[currentIndex + 1];
  const handleSwipe = (direction: 'left' | 'right') => {
    if (direction === 'right') {
      if (Math.random() > 0.5) {
        onMatch(currentProfile);
      }
    }
    setTimeout(() => setCurrentIndex(prev => prev + 1), 200);
  };
  const handleRewind = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };
  if (!currentProfile) {
    return <div className="flex flex-col items-center justify-center h-full text-center p-8" data-id="element-98">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        damping: 10,
        stiffness: 100
      }} className="w-24 h-24 bg-gradient-to-br from-orange-400 to-rose-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-rose-500/30" data-id="element-99">
          <Sparkles size={48} className="text-white" data-id="element-100" />
        </motion.div>
        <h3 className="text-xl font-bold text-zinc-900 mb-2" data-id="element-101">
          No more profiles
        </h3>
        <p className="text-zinc-500 mb-8" data-id="element-102">
          Check back later for more people nearby.
        </p>
        <button onClick={() => setCurrentIndex(0)} className="px-8 py-3 bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-full font-bold shadow-lg hover:shadow-xl transition-shadow" data-id="element-103">
          Start Over
        </button>
      </div>;
  }
  return <div className="flex flex-col h-full relative bg-zinc-50" data-id="element-104">
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-4 pb-2 z-30" data-id="element-105">
        <div className="flex items-center gap-1.5" data-id="element-106">
          <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-rose-500 rounded-lg flex items-center justify-center shadow-sm" data-id="element-107">
            <Sparkles size={18} className="text-white" data-id="element-108" />
          </div>
          <span className="text-xl font-black bg-gradient-to-r from-orange-500 to-rose-500 bg-clip-text text-transparent tracking-tight" data-id="element-109">
            spark
          </span>
        </div>
        <button onClick={() => setShowFilters(true)} className="p-2 bg-white rounded-full shadow-sm text-zinc-400 hover:text-rose-500 transition-colors" data-id="element-110">
          <SlidersHorizontal size={20} data-id="element-111" />
        </button>
      </div>

      {/* Card Stack Container */}
      <div className="flex-1 relative w-full max-w-sm mx-auto mt-2 mb-28 px-2" data-id="element-112">
        <AnimatePresence data-id="element-113">
          {nextProfile && <div className="absolute inset-0 scale-95 translate-y-2 opacity-50 pointer-events-none z-0 px-2" data-id="element-114">
              <ProfileCard key={nextProfile.id} user={nextProfile} onSwipe={() => {}} drag={false} data-id="element-115" />
            </div>}

          <ProfileCard key={currentProfile.id} user={currentProfile} onSwipe={handleSwipe} onDetailClick={() => setShowDetail(true)} data-id="element-116" />
        </AnimatePresence>
      </div>

      {/* Action Buttons */}
      <div className="absolute bottom-6 left-0 right-0 flex justify-center items-center gap-4 z-20 px-4" data-id="element-117">
        <button onClick={handleRewind} className="w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-amber-500 hover:bg-amber-50 hover:scale-110 transition-all duration-200 border border-zinc-100" aria-label="Rewind" data-id="element-118">
          <RotateCcw size={20} data-id="element-119" />
        </button>

        <button onClick={() => handleSwipe('left')} className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center text-rose-500 hover:bg-rose-50 hover:scale-110 transition-all duration-200 border border-zinc-100" aria-label="Pass" data-id="element-120">
          <X size={28} data-id="element-121" />
        </button>

        <button onClick={() => handleSwipe('right')} className="w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-blue-500 hover:bg-blue-50 hover:scale-110 transition-all duration-200 border border-zinc-100" aria-label="Super Like" data-id="element-122">
          <Star size={20} fill="currentColor" data-id="element-123" />
        </button>

        <button onClick={() => handleSwipe('right')} className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-rose-500 shadow-lg shadow-rose-500/30 flex items-center justify-center text-white hover:scale-110 transition-all duration-200" aria-label="Like" data-id="element-124">
          <Heart size={28} fill="currentColor" data-id="element-125" />
        </button>

        <button onClick={() => setShowBoost(true)} className="w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-purple-500 hover:bg-purple-50 hover:scale-110 transition-all duration-200 border border-zinc-100" aria-label="Boost" data-id="element-126">
          <Zap size={20} data-id="element-127" />
        </button>
      </div>

      {/* Modals */}
      <AnimatePresence data-id="element-128">
        {showDetail && <ProfileDetailModal user={currentProfile} onClose={() => setShowDetail(false)} onLike={() => {
        handleSwipe('right');
        setShowDetail(false);
      }} onPass={() => {
        handleSwipe('left');
        setShowDetail(false);
      }} onSuperLike={() => {
        handleSwipe('right');
        setShowDetail(false);
      }} data-id="element-129" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-130">
        {showFilters && <FilterModal onClose={() => setShowFilters(false)} data-id="element-131" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-132">
        {showBoost && <BoostModal onClose={() => setShowBoost(false)} data-id="element-133" />}
      </AnimatePresence>
    </div>;
}